import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Button } from 'reactstrap';
import { MONTHLY_ADD_ON_TIERS } from 'fym-shared/src/music24/monthlyAddOns';
import { TypedMemo } from '../../../common/types';
import { toggleSMLPricingModal } from '../../../../redux/slices/appInfo/slice';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { SMLLink } from '../../../music24/components/SMLLink/SMLLink';
import { helpArticleUrl } from '../../../support/helpCenter/helpCenterUrl';
import { emailInitials } from '../../../team/emailInitials';
import { useWorkspaces } from '../../../workspaces/useWorkspaces';
import { SMLSettingsTabId } from '../types';
import { AddOnAlert } from './AddOnAlert';
import { formatAddOnCents } from './addOnFormat';
import { monthlyAddOnMessages as messages } from './monthlyAddOnMessages';
import type { MonthlyAddOnsData } from './monthlyAddOnTypes';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the add-ons panel; each part uses its own slice of it */
import styles from './MonthlyAddOns.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** design monthly-addons b1: a member reads the add-ons and sees whom to ask. */
const MemberNote: React.FC<{ data: MonthlyAddOnsData }> = ({ data }) => {
  const intl = useIntl();
  const ownerEmail = useWorkspaces().active?.ownerEmail ?? null;
  const { posts, videoCredits } = data.current;
  const artists = data.extraArtists.offered ? data.extraArtists.current : 0;
  return (
    <>
      <div className={styles.list} data-testid="addons-member">
        {posts === 0 && videoCredits === 0 && artists === 0 ? (
          <span>{intl.formatMessage(messages.memberNone)}</span>
        ) : (
          <>
            <span>{intl.formatMessage(messages.postsName)}</span>
            <span className={styles.fig}>{intl.formatMessage(messages.memberValue, { count: posts })}</span>
            <span>{intl.formatMessage(messages.creditsName)}</span>
            <span className={styles.fig}>{intl.formatMessage(messages.memberValue, { count: videoCredits })}</span>
            {data.extraArtists.offered && (
              <>
                <span>{intl.formatMessage(messages.artistsName)}</span>
                <span className={styles.fig}>{intl.formatMessage(messages.memberArtists, { count: artists })}</span>
              </>
            )}
          </>
        )}
      </div>
      <div className={styles.body}>
        <div className={styles.owner}>
          {ownerEmail !== null && (
            <span className={styles.avatar} aria-hidden="true">
              {emailInitials(ownerEmail)}
            </span>
          )}
          <div data-clarity-mask="true">
            <b>{intl.formatMessage(messages.ownerOnly)}</b>
            {ownerEmail !== null && <span className={styles.muted}>{ownerEmail}</span>}
          </div>
        </div>
      </div>
    </>
  );
};

/** design monthly-addons b2: the plan is billed by a store; add-ons are sold on the web only. */
const StoreNote: React.FC<{ store: MonthlyAddOnsData['store'] }> = ({ store }) => {
  const intl = useIntl();
  return (
    <div className={styles.body} data-testid="addons-store">
      <AddOnAlert tone="info">
        {intl.formatMessage(store === 'googlePlay' ? messages.storeGooglePlay : messages.storeAppStore)}{' '}
        <a href={helpArticleUrl('billing', 'app-store-and-google-play')} target="_blank" rel="noopener noreferrer">
          {intl.formatMessage(messages.storeLink)}
        </a>
      </AddOnAlert>
    </div>
  );
};

/**
 * design monthly-addons b3: Free and the trial see the plan offer, and the price of one unit of each
 * add-on in its first tier (`MONTHLY_ADD_ON_TIERS`; QA 2026-10-28 skp-17).
 */
const NeedsPlanNote: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const openPlans = useCallback(() => dispatch(toggleSMLPricingModal({ shouldShow: true })), [dispatch]);
  return (
    <div className={styles.body} data-testid="addons-needs-plan">
      <p className={styles.muted}>
        {intl.formatMessage(messages.needsPlanPrices, {
          post: formatAddOnCents(intl, MONTHLY_ADD_ON_TIERS.posts[0]?.unitCents ?? 0),
          credit: formatAddOnCents(intl, MONTHLY_ADD_ON_TIERS.videoCredits[0]?.unitCents ?? 0),
        })}
      </p>
      <div>
        <Button color="primary" onClick={openPlans}>
          {intl.formatMessage(messages.seePlans)}
        </Button>
      </div>
    </div>
  );
};

/** design monthly-addons b4: a paid plan with no web subscription (an admin grant). */
const NoWebNote: React.FC = () => {
  const intl = useIntl();
  return (
    <div className={styles.body} data-testid="addons-no-web">
      <div>
        <SMLLink
          to={SMLRoutes.settings}
          params={{ tab: SMLSettingsTabId.support }}
          className="btn btn-outline-secondary"
        >
          {intl.formatMessage(messages.contactSupport)}
        </SMLLink>
      </div>
    </div>
  );
};

/** The lead line of the panel head for a state with no slider. A member of a team plan reads the artists too (b10). */
export function noticeLead(data: Pick<MonthlyAddOnsData, 'availability' | 'extraArtists'>) {
  switch (data.availability) {
    case 'notOwner':
      return data.extraArtists.offered ? messages.memberLeadTeam : messages.memberLead;
    case 'storeSubscription':
      return messages.storeLead;
    case 'needsPlan':
      return messages.needsPlanLead;
    case 'noWebSubscription':
      return messages.noWebLead;
    case 'available':
      return messages.lead;
  }
}

/** The body of the panel when the reader cannot buy add-ons (design monthly-addons b1-b4). */
const MonthlyAddOnsNoticeComponent: React.FC<{ data: MonthlyAddOnsData }> = ({ data }) => {
  switch (data.availability) {
    case 'notOwner':
      return <MemberNote data={data} />;
    case 'storeSubscription':
      return <StoreNote store={data.store} />;
    case 'needsPlan':
      return <NeedsPlanNote />;
    case 'noWebSubscription':
      return <NoWebNote />;
    case 'available':
      return null;
  }
};

export const MonthlyAddOnsNotice = TypedMemo(MonthlyAddOnsNoticeComponent);
